// src/render/lighting/torch-cone.ts — torch-cone texture key + sign-convention helper.
//
// Invariants:
//   - Phaser 4 ESM has NO default export (PATTERNS.md Pattern C). Namespace import only.
//   - The cone sprite is preloaded as a static 256×256 PNG by BootScene;
//     no dynamic texture build is needed here.
//   - Sim→Phaser facing-seam sign convention (PATTERNS.md Pattern D):
//     phaserAngleDeg = -simFacingRad * 180/PI.
//
//     nipplejs angle.radian is CCW-positive, y-UP (math convention):
//       0=RIGHT, π/2=UP, π=LEFT, 3π/2=DOWN.
//     Phaser stamp `angle` is CW-positive, y-DOWN (screen convention):
//       0=RIGHT, 90=DOWN, 180=LEFT, -90=UP.
//     Therefore phaserAngle = -simFacing (converted to degrees).
//     The same sign is locked for the character-sprite rotation in
//     src/render/scenes/GameScene.ts:198-209 and in
//     src/render/sprites/__tests__/character-sprite.test.ts — flipping the sign
//     here without re-running those tests is a silent-regression risk (T-02-10).
//
// Source: Phase 2 RESEARCH.md §Pattern 2, UI-SPEC.md §Light Mask Palette.

// Phaser 4 ESM has NO default export (PATTERNS.md Pattern C).
import * as Phaser from 'phaser';

/** Texture key for the placeholder torch-cone PNG preloaded by BootScene. */
export const TORCH_CONE_TEXTURE_KEY = 'light.torch-cone';

/** Texture key for the placeholder permanent-glow PNG preloaded by BootScene. */
export const PERMANENT_GLOW_TEXTURE_KEY = 'light.permanent-glow';

/** Texture key for the placeholder motion-glow PNG preloaded by BootScene. */
export const MOTION_GLOW_TEXTURE_KEY = 'light.motion-glow';

/**
 * Native PNG pixel radius for the radial-gradient glow sprites (128×128 placeholder
 * PNGs). Used to scale stamp draws to a light's authored tile-radius. UI-SPEC
 * §Light Mask Palette locked value.
 */
export const GLOW_SPRITE_NATIVE_RADIUS_PX = 64; // half of 128

/**
 * Native PNG radius for the torch-cone sprite (256×256 placeholder). The cone
 * extends from sprite centre to the +x edge; stamp rotation is applied around
 * the sprite centre. Authored by scripts/generate-placeholder-light-sprites.mjs.
 */
export const TORCH_CONE_NATIVE_RADIUS_PX = 128;

/**
 * Converts a sim facing (CCW-positive, y-UP, radians) into a Phaser stamp angle
 * (CW-positive, y-DOWN, degrees). See the invariants block at top of file for
 * the locked derivation.
 *
 * Regression: if this sign flips silently, the torch cone will point the wrong
 * way on-device. Test 7 of mask.test.ts locks it at the stamp call site;
 * character-sprite.test.ts cardinal-orientation suite locks the equivalent
 * Container rotation at GameScene.ts:209.
 */
export function simFacingToPhaserAngleDeg(facingRad: number): number {
  return -facingRad * (180 / Math.PI);
}

// Phaser is imported to keep the namespace import token present for tooling
// consistency with the rest of src/render/lighting/**. Referenced here so
// tsc --noEmit doesn't strip the import in type-only contexts.
void Phaser;
